import type { ApolloCache, Reference } from '@base-app/web2'

// The list arguments a new record belongs to, e.g. { clientId, applicationId }. A cached list
// that was fetched without one of these arguments is unfiltered on it, so the record belongs.
export type ListScope = Record<string, string | undefined>

// Apollo stores each argument combination as `field({"clientId":"…","limit":10,"offset":0})`
function parseStoreFieldArgs(storeFieldName: string): Record<string, unknown> {
  const open = storeFieldName.indexOf('(')
  if (open === -1) {
    return {}
  }
  try {
    return JSON.parse(storeFieldName.slice(open + 1, -1)) ?? {}
  } catch {
    return {}
  }
}

function inScope(args: Record<string, unknown>, scope: ListScope): boolean {
  return Object.entries(scope).every(
    ([key, value]) => args[key] === undefined || args[key] === value,
  )
}

// Create forms answer optimistically with a `temp-…` id before the server replies
export const OPTIMISTIC_ID_PREFIX = 'temp-'

function isSearched(args: Record<string, unknown>): boolean {
  return typeof args.search === 'string' && args.search.trim() !== ''
}

export function belongsToList(args: Record<string, unknown>, scope: ListScope): boolean {
  if (Number(args.offset ?? 0) !== 0) {
    // Later pages shift on the next fetch; inserting here would duplicate the record
    return false
  }
  if (isSearched(args)) {
    // A searched list holds only matches, and the API decides what matches
    return false
  }
  return inScope(args, scope)
}

// Drop every cached searched list of `fieldName` in scope, so an open search refetches and
// shows what the API says now. Call it with the server's reply, never during the optimistic
// pass (see prependToLists).
export function dropSearchedLists(cache: ApolloCache, fieldName: string, scope: ListScope = {}) {
  cache.modify({
    fields: {
      [fieldName](existing: readonly Reference[] = [], { storeFieldName, DELETE }) {
        const args = parseStoreFieldArgs(storeFieldName)
        return isSearched(args) && inScope(args, scope) ? DELETE : existing
      },
    },
  })
}

// Like prependToLists, for lists the API orders with pinned rows first (locked functions): the
// record goes right after the leading rows whose `locked` is true, never above them. Only a first
// guess for the position: the caller refetches the active lists once the server answers.
export function insertAfterLockedRows(
  cache: ApolloCache,
  fieldName: string,
  item: { __typename?: string; id: string },
  scope: ListScope = {},
) {
  cache.modify({
    fields: {
      [fieldName](existing: readonly Reference[] = [], { storeFieldName, toReference, readField }) {
        const args = parseStoreFieldArgs(storeFieldName)
        if (!belongsToList(args, scope)) {
          return existing
        }
        const ref = toReference(item as { __typename: string; id: string }, true)
        if (!ref || existing.some(r => readField('id', r) === item.id)) {
          return existing
        }
        let at = 0
        while (at < existing.length && readField('locked', existing[at]) === true) {
          at++
        }
        return [...existing.slice(0, at), ref, ...existing.slice(at)]
      },
    },
  })
}

/**
 * Put a just-created record at the top of every cached first page of `fieldName` whose
 * arguments it matches, as a normalized reference so later edits to the record show in
 * every list. Lists for other clients, apps or pages are left alone. Searched lists in scope
 * are dropped from the cache instead, so an open search refetches and shows the record if
 * the API says it matches. That drop waits for the server's reply: dropped during the
 * optimistic pass, the open query never refetches and keeps showing its old rows.
 */
export function prependToLists(
  cache: ApolloCache,
  fieldName: string,
  item: { __typename?: string; id: string },
  scope: ListScope = {},
) {
  const optimistic = item.id.startsWith(OPTIMISTIC_ID_PREFIX)
  cache.modify({
    fields: {
      [fieldName](
        existing: readonly Reference[] = [],
        { storeFieldName, toReference, readField, DELETE },
      ) {
        const args = parseStoreFieldArgs(storeFieldName)
        if (isSearched(args)) {
          return inScope(args, scope) && !optimistic ? DELETE : existing
        }
        if (!belongsToList(args, scope)) {
          return existing
        }
        const ref = toReference(item as { __typename: string; id: string }, true)
        if (!ref || existing.some(r => readField('id', r) === item.id)) {
          return existing
        }
        return [ref, ...existing]
      },
    },
  })
}
